import { getQueryClient } from '@/lib/reactQuery'
import {
  IPasswordUpdateParam,
  IUserJoinParam,
  IUserSocialJoinParam,
  IUserUpdateParam,
} from '@/service/models/user'
import { queryKeys } from '@/service/queries'
import { userApi } from '@/service/user'
import { IMutationContext } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const useGetMe = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.USER.me(),
    queryFn: () => userApi.getMe(),
    select: ({ data }) => data,
    staleTime: 30 * 60 * 1000, // 30분
    // gcTime: Infinity,
    enabled: !!enabled,
  })
}

export const useUserUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (param: IUserUpdateParam) => userApi.update(param),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.USER.all })
    },
  })
}

export const useJoin = () => {
  const { mutateAsync: onDefaultJoin } = useMutation({
    mutationFn: (param: IUserJoinParam) => userApi.join(param),
  })

  const { mutateAsync: onSocialJoin } = useMutation({
    mutationFn: ({ param, token }: { param: IUserSocialJoinParam; token: string }) =>
      userApi.socialJoin(param, token),
  })

  return {
    onDefaultJoin,
    onSocialJoin,
  }
}

/** 로그인아이디 중복 확인 */
export const useCheckLoginId = () => {
  return useMutation({
    mutationFn: (email: string) => {
      return userApi.checkLoginId(email)
    },
    onMutate: () => {
      const context: IMutationContext = { skipToast: true }
      return context
    },
  })
}

/** 이메일 중복 확인 */
export const useCheckEmail = () => {
  return useMutation({
    mutationFn: (email: string) => {
      return userApi.checkEmail(email)
    },
    onMutate: () => {
      const context: IMutationContext = { skipToast: true }
      return context
    },
  })
}

/** 닉네임 중복 확인 */
export const useCheckNickname = () => {
  return useMutation({
    mutationFn: (nickname: string) => {
      return userApi.checkNickname(nickname)
    },
    onMutate: () => {
      const context: IMutationContext = { skipToast: true }
      return context
    },
  })
}

/** 비밀번호 변경 */
export const useUpdatePassword = () => {
  const queryClient = getQueryClient()
  return useMutation({
    mutationFn: (params: IPasswordUpdateParam) => {
      return userApi.updatePassword(params)
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: queryKeys.USER.me() })
    },
  })
}
